// ---------------------------------------------------------------------------
// FloatingCta Renderer (editor key: FloatingCta)
// ---------------------------------------------------------------------------
// Persistent pill-style CTA anchored to a screen corner. Published as a
// single `<a>` so the markup works without JavaScript and screen readers
// receive a real link. `href` is filtered through the `url()` safelist;
// unsupported schemes (e.g. `javascript:`) collapse to `#`. Position is a
// class-driven enum so CSS controls layout; we never emit inline `top` or
// `bottom` coordinates from editor input.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface FloatingCtaProps {
  label?: string;
  href?: string;
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  variant?: "primary" | "secondary" | "accent";
  icon?: string;
  ariaLabel?: string;
}

const ALLOWED_POSITIONS = new Set([
  "bottom-right",
  "bottom-left",
  "top-right",
  "top-left",
]);
const ALLOWED_VARIANTS = new Set(["primary", "secondary", "accent"]);

function normalizePosition(value: unknown): string {
  if (typeof value === "string" && ALLOWED_POSITIONS.has(value)) return value;
  return "bottom-right";
}

function normalizeVariant(value: unknown): string {
  if (typeof value === "string" && ALLOWED_VARIANTS.has(value)) return value;
  return "primary";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as FloatingCtaProps;

  const label = typeof p.label === "string" ? p.label.trim() : "";
  if (!label) {
    return "<!-- floating-cta missing label -->";
  }

  const href = typeof p.href === "string" && p.href.trim() ? p.href.trim() : "#";
  const position = normalizePosition(p.position);
  const variant = normalizeVariant(p.variant);
  const icon = typeof p.icon === "string" ? p.icon.trim() : "";
  const ariaLabel =
    typeof p.ariaLabel === "string" && p.ariaLabel.trim() ? p.ariaLabel.trim() : label;

  const iconHtml = icon
    ? `\n  <span class="ease-floating-cta__icon" aria-hidden="true">${text(icon)}</span>`
    : "";

  return `<a class="ease-floating-cta ease-floating-cta--${attr(position)} ease-floating-cta--${attr(variant)}" href="${url(href)}" aria-label="${attr(ariaLabel)}" data-ease-position="${attr(position)}" data-ease-variant="${attr(variant)}">${iconHtml}
  <span class="ease-floating-cta__label">${text(label)}</span>
</a>`;
}

registerRenderer("floating-cta", { render });
